import { useEffect, useState } from "react";
import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/auth-context";
import type { IdentityProvider } from "../auth/session";
import { CenteredPage } from "../components/centered-page";
import { ErrorMessage } from "../components/error-message";
import { Button } from "../components/ui/button";
// Vite liefert bei Bild-Importen die (gehashte) URL der Datei als string.
// Offizielle, unveränderte Anbieter-Logos, siehe assets/idp/README.md.
import googleG from "../assets/idp/google-g.png";
import microsoftSymbol from "../assets/idp/microsoft-symbol.svg";

// getRouteApi("/") liefert typsichere Hooks dieser Route (z. B. useSearch),
// ohne die Route-Definition zu importieren (vermeidet zirkuläre Importe).
const route = getRouteApi("/");

// Startseite mit Anbieter-Buttons (Entscheidung 37). Später zeigt dieselbe
// Seite unter /t/{subdomain} die Login-Konfiguration des Mandanten.
export function StartPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const router = useRouter();
  const { returnTo } = route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Zurück-Taste nach der Umleitung zu Cognito/Google: Der Browser stellt die
  // Seite oft unverändert aus seinem Zwischenspeicher her (bfcache) – samt
  // `busy = true`, also ausgegrauten Buttons. `pageshow` mit `persisted` meldet
  // genau diesen Fall; dann die Buttons wieder freigeben.
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent): void {
      if (event.persisted) setBusy(false);
    }
    window.addEventListener("pageshow", onPageShow);
    // Aufräumfunktion: entfernt den Listener, wenn die Seite verlassen wird.
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  async function login(provider?: IdentityProvider): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const target = returnTo ?? "/select";
      // Cognito: verlässt hier die Seite. Lokal: direkt angemeldet.
      await auth.login({ ...(provider && { provider }), returnTo: target });
      router.history.push(target);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    // intro: animiertes Logo und Einblendung, nur hier (Entscheidung 41)
    <CenteredPage intro>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl text-ink">{t("start.title")}</h1>
          <p className="text-ink-muted">{t("start.subtitle")}</p>
        </div>

        {auth.session.kind === "local" ? (
          <div className="flex flex-col gap-3">
            <Button size="lg" disabled={busy} onClick={() => void login()}>
              {t("start.devLogin")}
            </Button>
            <p className="text-xs text-ink-muted">{t("start.devHint")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              variant="idp"
              size="lg"
              disabled={busy}
              onClick={() => void login("Google")}
              className="gap-2.5"
            >
              {/* alt="": Logo ist Schmuck, der Buttontext nennt den Anbieter */}
              <img src={googleG} alt="" className="size-5 shrink-0" />
              {t("start.withGoogle")}
            </Button>
            <Button
              variant="idp"
              size="lg"
              disabled={busy}
              onClick={() => void login("Microsoft")}
              className="gap-2.5"
            >
              <img src={microsoftSymbol} alt="" className="size-5 shrink-0" />
              {t("start.withMicrosoft")}
            </Button>
            {/* Ohne Anbieter: Cognito Managed Login (Rückfall, Entscheidung 37) */}
            <Button variant="link" disabled={busy} onClick={() => void login()}>
              {t("start.otherMethods")}
            </Button>
          </div>
        )}

        {error !== null && <ErrorMessage error={error} />}

        {/* Zugang gibt es in v1 per Einladungslink oder Self-Service-Mandant (Entscheidung 26) */}
        <p className="border-t border-line pt-5 text-sm text-ink-muted">{t("start.noAccess")}</p>
      </div>
    </CenteredPage>
  );
}
